import { useEffect, useState } from 'react';
import { Modal, TextInput, NumberInput, Select, Button, Group, Stack, Text } from '@mantine/core';
import { Check } from 'lucide-react';
import type { Group as GroupType, Member } from '../api';

interface Props {
  opened: boolean;
  member: Member | null;
  groups: GroupType[];
  saving?: boolean;
  onClose: () => void;
  onSubmit: (data: { number: number | null; name: string; group_id: number | null }) => void;
}

export function MemberFormModal({ opened, member, groups, saving, onClose, onSubmit }: Props) {
  const [number, setNumber] = useState<string | number>('');
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setNumber(member?.number ?? '');
    setName(member?.name ?? '');
    // Člen bez skupiny (kopie z jiné epochy) má group_id null — String(null) by dalo "null"
    // a při uložení NaN, takže se tu drží prázdná volba.
    setGroupId(member?.group_id != null ? String(member.group_id) : (member ? null : (groups[0] ? String(groups[0].id) : null)));
    setNameError(null);
  }, [opened, member, groups]);

  // Formulář, ne onKeyDown — Enter tak odešle nativně a bez ručního odchytávání.
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Doplň jméno');
      return;
    }
    onSubmit({
      number: number === '' || number === null ? null : Number(number),
      name: trimmed,
      group_id: groupId ? Number(groupId) : null,
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} title={member ? 'Upravit člena' : 'Nový člen'}>
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <NumberInput
            label="Číslo"
            placeholder="např. 26"
            value={number}
            onChange={setNumber}
            min={1}
            allowDecimal={false}
            description="Bez čísla se člen nezobrazí v mobilní aplikaci."
          />
          <TextInput
            label="Jméno"
            placeholder="Jméno a příjmení"
            value={name}
            error={nameError}
            onChange={(e) => { setName(e.currentTarget.value); setNameError(null); }}
            data-autofocus
          />
          <Select
            label="Skupina"
            placeholder="Bez skupiny"
            description={groupId ? undefined : 'Bez skupiny se člen v žebříčcích nezobrazí.'}
            data={groups.map((g) => ({ value: String(g.id), label: g.name }))}
            value={groupId}
            onChange={setGroupId}
            clearable
          />
          {groups.length === 0 && (
            <Text c="dimmed" fz="sm">Zatím tu není žádná skupina — člena můžeš založit i bez ní.</Text>
          )}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onClose} type="button">Zrušit</Button>
            <Button type="submit" leftSection={<Check size={14} />} loading={saving}>
              {member ? 'Uložit změny' : 'Přidat člena'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
